import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../api/clients'
import { getClientSystemInfo } from '../../api/0x0001/systeminfo'
import { getClientFragmentedSystemInfo, IFragmentedSystemInfo } from '../../api/0x0002/fragmented-systeminfo'
import { getString } from '../../locale'
import { readableMemory } from '../../utils/memory'
import { readableTime } from '../../utils/time'

type MetricKey =
    | 'cooling_unit_active_time'
    | 'pump_active_time'
    | 'boiler_active_time'
    | 'fan_active_time'
    | 'circulation_pump_active_time'
    | 'uptime'
    | 'free_heap'

type Props = {
    clientId: IClientUID
    variant: 'standard' | 'fragmented'
}

type State = {
    loading: boolean
    lastUpdatedAt?: number
    series: Record<MetricKey, number[]>
}

const HISTORY_LIMIT = 45
const POLL_INTERVAL_MS = 2000

const METRIC_KEYS: MetricKey[] = [
    'cooling_unit_active_time',
    'pump_active_time',
    'boiler_active_time',
    'fan_active_time',
    'circulation_pump_active_time',
    'uptime',
    'free_heap',
]

type MetricLocaleKey =
    | 'push.metric.cooling_unit_active_time'
    | 'push.metric.pump_active_time'
    | 'push.metric.boiler_active_time'
    | 'push.metric.fan_active_time'
    | 'push.metric.circulation_pump_active_time'
    | 'system_info.uptime'
    | 'system_info.free_heap'

const METRIC_LABEL_KEYS: Record<MetricKey, MetricLocaleKey> = {
    cooling_unit_active_time: 'push.metric.cooling_unit_active_time',
    pump_active_time: 'push.metric.pump_active_time',
    boiler_active_time: 'push.metric.boiler_active_time',
    fan_active_time: 'push.metric.fan_active_time',
    circulation_pump_active_time: 'push.metric.circulation_pump_active_time',
    uptime: 'system_info.uptime',
    free_heap: 'system_info.free_heap',
}

class MonitoringCharts extends Component<Props, State> {
    mounted = false

    state: State = {
        loading: true,
        lastUpdatedAt: undefined,
        series: {
            cooling_unit_active_time: [],
            pump_active_time: [],
            boiler_active_time: [],
            fan_active_time: [],
            circulation_pump_active_time: [],
            uptime: [],
            free_heap: [],
        },
    }

    setState: Component<Props, State>['setState'] = (state, callback) => {
        if (!this.mounted) return
        return super.setState(state as State, callback)
    }

    componentDidMount = async () => {
        this.mounted = true
        await this.loadData()
    }

    componentWillUnmount = () => {
        this.mounted = false
    }

    private findFragment<T extends keyof IFragmentedSystemInfo[number]>(systemInfo: IFragmentedSystemInfo, prop: T): IFragmentedSystemInfo[number][T] | undefined {
        for (const fragmentVersion in systemInfo) {
            const fragment = systemInfo[fragmentVersion]
            if (!fragment) continue
            if (fragment[prop] !== undefined) {
                return fragment[prop]
            }
        }
        return undefined
    }

    private async loadData() {
        if (!this.mounted) return
        try {
            const snapshot =
                this.props.variant === 'standard'
                    ? await this.loadStandardSnapshot()
                    : await this.loadFragmentedSnapshot()

            this.setState(prev => {
                const nextSeries = { ...prev.series }
                for (const key of METRIC_KEYS) {
                    const nextPoints = [...nextSeries[key], snapshot[key]]
                    if (nextPoints.length > HISTORY_LIMIT) nextPoints.shift()
                    nextSeries[key] = nextPoints
                }
                return {
                    loading: false,
                    lastUpdatedAt: Date.now(),
                    series: nextSeries,
                }
            })
        } catch (err) {
            this.setState({ loading: false })
            toast.error((err as Error).message)
        }

        if (this.mounted) {
            setTimeout(this.loadData.bind(this), POLL_INTERVAL_MS)
        }
    }

    private async loadStandardSnapshot(): Promise<Record<MetricKey, number>> {
        const info = (await getClientSystemInfo(this.props.clientId)).data
        return {
            cooling_unit_active_time: Number(info.compressorActiveTime || 0),
            pump_active_time: Number(info.pumpActiveTime || 0),
            boiler_active_time: Number(info.boilerActiveTime || 0),
            fan_active_time: Number(info.fanActiveTime || 0),
            circulation_pump_active_time: Number(info.circPumpActiveTime || 0),
            uptime: Number(info.uptime || 0),
            free_heap: Number(info.freeHeap || 0),
        }
    }

    private async loadFragmentedSnapshot(): Promise<Record<MetricKey, number>> {
        const info = (await getClientFragmentedSystemInfo(this.props.clientId)).data
        const common = this.findFragment(info, 'common')
        const alu = this.findFragment(info, 'aluBlockCoolingUnit')
        const ice = this.findFragment(info, 'iceBlockCoolingUnit')
        const soda = this.findFragment(info, 'sodaUnit')
        const heating = this.findFragment(info, 'heatingUnit')
        const ambient = this.findFragment(info, 'ambientUnit')
        const circulation = this.findFragment(info, 'circulationPumpUnit')

        return {
            cooling_unit_active_time: Number((ice?.compressorActiveTime ?? alu?.compressorActiveTime) || 0),
            pump_active_time: Number(soda?.pumpActiveTime || 0),
            boiler_active_time: Number(heating?.boilerActiveTime || 0),
            fan_active_time: Number(ambient?.fanActiveTime || 0),
            circulation_pump_active_time: Number(circulation?.circPumpActiveTime || 0),
            uptime: Number(common?.uptime || 0),
            free_heap: Number(common?.freeHeap || 0),
        }
    }

    private formatValue(key: MetricKey, value: number) {
        if (key === 'free_heap') return readableMemory(value)
        return readableTime(value)
    }

    private metricLabel(key: MetricKey) {
        return getString(METRIC_LABEL_KEYS[key])
    }

    render() {
        const { loading, series, lastUpdatedAt } = this.state
        if (loading) {
            return (
                <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
                    <div className="text-lg font-semibold dark:text-white">{getString('monitoring.live_title')}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{getString('common.loading')}...</div>
                </div>
            )
        }

        return (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
                <div className="flex justify-between items-center mb-3">
                    <div className="text-lg font-semibold dark:text-white">{getString('monitoring.live_title')}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        {lastUpdatedAt ? `${getString('monitoring.updated_at')}: ${new Date(lastUpdatedAt).toLocaleTimeString()}` : ''}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {METRIC_KEYS.map(key => {
                        const points = series[key]
                        const current = points.length > 0 ? points[points.length - 1] : 0
                        return (
                            <div key={key} className="rounded-lg border dark:border-gray-700 p-3">
                                <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">{this.metricLabel(key)}</div>
                                <div className="text-sm font-semibold dark:text-white mb-2">{this.formatValue(key, current)}</div>
                                {this.renderSparkline(points)}
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    private renderSparkline(points: number[]) {
        const width = 260
        const height = 56
        if (points.length < 2) {
            return (
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-14">
                    <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 3" />
                </svg>
            )
        }

        const min = Math.min(...points)
        const max = Math.max(...points)
        const range = Math.max(max - min, 1)
        const path = points
            .map((v, i) => {
                const x = (i / (points.length - 1)) * width
                const y = height - ((v - min) / range) * (height - 6) - 3
                return `${x},${y}`
            })
            .join(' ')

        return (
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-14">
                <polyline fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={path} />
            </svg>
        )
    }
}

export default MonitoringCharts
